/** @jsxImportSource preact */
import { useState, useEffect, useCallback, useMemo, useRef } from 'preact/hooks'
import { CONSOLIDATOR_REFUND_FEE, computeConsolidatorRefundFee, resolveDomesticGstRate } from '@plt/refund-quote'
import { config } from '../config'
import { useFrontContext, extractBookingId, isFrontEmbedded } from '../providers/front'
import { useAuth } from '../providers/auth'
import { missingSupplierFeeMessage } from './missing-supplier-fee-message'
import { QuoteLoadingPanel } from './QuoteLoadingPanel'
import { retrieveRefundQuote } from './retrieve-refund-quote'

type FormAction =
  | 'refund-calculator'
  | 'change'
  | 'cancellation'
  | 'credit-redemption'
  | 'cancel-to-credit'
  | 'change-qc'
  | 'cancellation-qc'
  | 'change-calculator'

type FormStatus = 'idle' | 'loading' | 'success' | 'error'

interface BookingDetails {
  value: number
  grossFareCost?: number
  cancelationPenalty?: number
  unconvertedAirlineFee?: { amount: number; currency: string }[]
  travellersTotal?: number
  infantsTotal?: string
  currency?: string
  payment?: {
    totalPaid: number
    discountAmount: number
    deposit: number
    pastDue: number
  }
  // General booking info
  origin?: string
  destination?: string
  departureDate?: string
  returnDate?: string
  departureALTime?: string
  returnALTime?: string
  pnr?: string
  airlineReference?: string
  eticketNumber?: string
  // Cancellation and refund structure
  cancellationAndRefund?: {
    xlLayByTerminationFee?: number
    xlCancellationDate?: string
    xlPartialBookingCancellationDate?: string
    xlPartialBookingEticketNumbers?: string
    xlCustomerTotalPaidToDate?: number
    xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided?: string
    xlFinalEmailSentToCustomer?: string
    xlCancelledWithSupplierRequestedRefund?: string
    rfSupplierTotalCashRefund?: number
    rfSupplierExpectRefundBackDate?: string
    rfCustomerAdvisedRefundWouldBeSentDate?: string
    refundAmountToCustomer?: number
    cancellationReason?: string
    refundProtectRefundDate?: string
    refundProtectAmountReceived?: number
  }

  // Voluntary change structure
  voluntaryChange?: {
    vcCustomerChangeFeeCredFdPaid?: number
    vcCustomerChangeFeeCredFdPaidDate?: string
    vcSupplierChangeFeePaid?: number
    vcSupplierChangeFeePaidDate?: string
    vcPltCreditUpdatedPdFields?: string
    vcSendUpdatedTripConfirmation?: string
    vcQcComplete?: string
  }

  // Cancel to credit structure
  cancelToCredit?: {
    airlineVoucherSentRequestToConvert?: string
    airlineVoucherRedeemBefore?: string
    airlineVoucherExpiration?: string
    airlineVoucherCustomerFullyPaid?: string
    airlineVoucherSendConfirmationEmail?: string
    airlineVoucherEticketNumbersInCredit?: string
    airlineVoucherAirlineFeeToRedeemPerPerson?: number
    airlineVoucherPltFeeToRedeemPerPerson?: number
  }

  // Credit redemption structure
  creditRedemption?: {
    vcCustomerChangeFeeCredFdPaid?: number
    vcCustomerChangeFeeCredFdPaidDate?: string
    vcSupplierChangeFeePaid?: number
    vcSupplierChangeFeePaidDate?: string
    vcPltCreditUpdatedPdFields?: string
    airlineVoucherEticketNumbers?: string
    airlineVoucherAirlineReferenceNumber?: string
    airlineVoucherNewPnr?: string
  }
}

interface RefundQuoteTicket {
  number: string
  isVoidable?: boolean
  isRefundable?: boolean
  isAutomatedRefundsEligible?: boolean
  isChangeable?: boolean
  ticketStatusCode?: string
  refundPenalties?: Array<{
    applicability: string
    penalty?: { amount: string; currencyCode: string }
    source?: string
    conditionsApply?: boolean
  }>
  refundTaxes?: Array<{ taxCode: string; amount: string }>
  refundTotals?: { subtotal: string; taxes: string; total: string; currencyCode: string; fees: string }
  refundFee?: { amount: string; currencyCode: string }
  exchangePenalties?: Array<{
    applicability: string
    penalty?: { amount: string; currencyCode: string }
    source?: string
    conditionsApply?: boolean
  }>
}

type FareRuleEntry = { category?: string; title: string; text: string }
type FareComponentSummary = { originDestination: string; fareBasis: string; airline: string; rules: FareRuleEntry[] }

interface RefundQuoteData {
  airlineControl?: boolean
  partialData?: boolean
  tickets?: RefundQuoteTicket[]
  travelersByTicket?: Record<string, string>
  allTaxesByTicket?: Record<string, Array<{ taxCode: string; amount: string; currencyCode: string }>>
  fareRules?: FareComponentSummary[]
  fareRulesError?: boolean
  owningPcc?: string
  ticketingPcc?: string
  ticketPayments?: Record<string, { subtotal: number; taxes: number; total: number; commission?: number }>
}

export function ChangeAndCancellation() {
  const [bookingId, setBookingId] = useState('')
  const [selectedAction, setSelectedAction] = useState<FormAction>('change')
  const [formStatus, setFormStatus] = useState<FormStatus>('idle')
  // A second click lands before the disabled button re-renders; this drops it.
  const submitting = useRef(false)
  // Clears the success message; cancelled on reset and on the next submit so it can't end a later status.
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Each submit writes the deal and adds a Pipedrive note, so the buttons stay off until the success
  // message clears; after an error they come back straight away.
  const submitBlocked = formStatus === 'loading' || formStatus === 'success'
  const [errorMessage, setErrorMessage] = useState('')
  const [fetchingBooking, setFetchingBooking] = useState(false)
  const frontContext = useFrontContext()
  const isFrontApp = isFrontEmbedded()
  const { user } = useAuth()

  const [totalPaidAmount, setTotalPaidAmount] = useState('')
  const [supplierFee, setSupplierFee] = useState('')

  const [passengersCount, setPassengersCount] = useState('')
  const [salesPrice, setSalesPrice] = useState('')
  const [bookingDetails, setBookingDetails] = useState<BookingDetails | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const [currency, setCurrency] = useState('USD')

  // Refund quote state
  const [refundQuoteData, setRefundQuoteData] = useState<RefundQuoteData | null>(null)
  const [refundQuoteLoading, setRefundQuoteLoading] = useState(false)
  const [refundQuoteError, setRefundQuoteError] = useState('')
  const [refundConditionsExpanded, setRefundConditionsExpanded] = useState(true)
  const [fareRulesExpanded, setFareRulesExpanded] = useState(false)
  const [refundTotalsExpanded, setRefundTotalsExpanded] = useState(true)
  const [changeConditionsExpanded, setChangeConditionsExpanded] = useState(true)
  const [changeFareRulesExpanded, setChangeFareRulesExpanded] = useState(false)

  // Change calculator fields
  const [changeFeePerPassenger, setChangeFeePerPassenger] = useState('')
  const [fareDifference, setFareDifference] = useState('')
  const [changeCalculatorConsolidatorFeePerPassenger, setChangeCalculatorConsolidatorFeePerPassenger] = useState('75')

  // Change form fields
  const [changeVcCustomerChangeFee, setChangeVcCustomerChangeFee] = useState('')
  const [changeVcCustomerChangeFeePaidDate, setChangeVcCustomerChangeFeePaidDate] = useState('')
  const [changeVcSupplierChangeFee, setChangeVcSupplierChangeFee] = useState('')
  const [changeVcSupplierChangeFeePaidDate, setChangeVcSupplierChangeFeePaidDate] = useState('')
  const [changeVcPltCreditUpdatedFields, setChangeVcPltCreditUpdatedFields] = useState('vc-yes-completed')
  const [changeOrigin, setChangeOrigin] = useState('')
  const [changeDestination, setChangeDestination] = useState('')
  const [changeDepartDate, setChangeDepartDate] = useState('')
  const [changeReturnDate, setChangeReturnDate] = useState('')
  const [changeDepartTime, setChangeDepartTime] = useState('')
  const [changeReturnTime, setChangeReturnTime] = useState('')
  const [changeRecordLocatorPnr, setChangeRecordLocatorPnr] = useState('')
  const [changeAirlineReferenceNo, setChangeAirlineReferenceNo] = useState('')
  const [changeETicketNo, setChangeETicketNo] = useState('')
  const [changeNote, setChangeNote] = useState('')

  // Credit redemption form fields
  const [vcCustomerChangeFee, setVcCustomerChangeFee] = useState('')
  const [vcCustomerChangeFeePaidDate, setVcCustomerChangeFeePaidDate] = useState('')
  const [vcSupplierChangeFee, setVcSupplierChangeFee] = useState('')
  const [vcSupplierChangeFeePaidDate, setVcSupplierChangeFeePaidDate] = useState('')
  const [vcPltCreditUpdatedFields, setVcPltCreditUpdatedFields] = useState('vc-yes-completed')
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [departDate, setDepartDate] = useState('')
  const [returnDate, setReturnDate] = useState('')
  const [departTime, setDepartTime] = useState('')
  const [returnTime, setReturnTime] = useState('')
  const [note, setNote] = useState('')

  // Change QC form fields
  const [vcQcComplete, setVcQcComplete] = useState('yes')

  // Set default note prefill
  useEffect(() => {
    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `updated by ${username} in Front`
      setChangeNote(notePrefill)
      setNote(notePrefill)
    }
  }, [user])

  // Cancellation form fields
  const [xlLayByTerminationFee, setXlLayByTerminationFee] = useState('')
  const [xlCancellationDate, setXlCancellationDate] = useState('')
  const [xlPartialBookingCancellationDate, setXlPartialBookingCancellationDate] = useState('')
  const [xlPartialBookingEticketNumbers, setXlPartialBookingEticketNumbers] = useState('')
  const [xlCustomerTotalPaidToDate, setXlCustomerTotalPaidToDate] = useState('')
  const [xlStripeSubscriptionCancelled, setXlStripeSubscriptionCancelled] = useState(false)
  const [xlFinalEmailSentToCustomer, setXlFinalEmailSentToCustomer] = useState('no')
  const [xlCancelledWithSupplierRefund, setXlCancelledWithSupplierRefund] = useState('yes')
  const [rfSupplierTotalCashRefund, setRfSupplierTotalCashRefund] = useState('')
  const [rfSupplierExpectRefundBackDate, setRfSupplierExpectRefundBackDate] = useState('')
  const [rfAmountPromisedToCustomer, setRfAmountPromisedToCustomer] = useState('')
  // Mirrors CancellationSchema's superRefine: a promised amount makes the advised date mandatory.
  const refundPromisedToCustomer = (parseFloat(rfAmountPromisedToCustomer) || 0) > 0
  const [rfCustomerAdvisedRefundDate, setRfCustomerAdvisedRefundDate] = useState('')
  const [cancellationReason, setCancellationReason] = useState('')

  // Credit redemption form fields - using the voluntary change fields
  // plus adding specific airline voucher fields for credit redemption
  const [airlineVoucherNewPnr, setAirlineVoucherNewPnr] = useState('')
  const [airlineVoucherAirlineReferenceNumber, setAirlineVoucherAirlineReferenceNumber] = useState('')
  const [airlineVoucherEticketNumbers, setAirlineVoucherEticketNumbers] = useState('')

  // Converting to airline credit form fields
  const [airlineVoucherSentRequest, setAirlineVoucherSentRequest] = useState('yes')
  const [airlineVoucherRedeemBefore, setAirlineVoucherRedeemBefore] = useState('')
  const [airlineVoucherExpiration, setAirlineVoucherExpiration] = useState('')
  const [airlineVoucherCustomerFullyPaid, setAirlineVoucherCustomerFullyPaid] = useState('yes')
  const [airlineVoucherSendConfirmationEmail, setAirlineVoucherSendConfirmationEmail] = useState('yes-sent')
  const [airlineVoucherEticketNumbersInCredit, setAirlineVoucherEticketNumbersInCredit] = useState('')
  const [airlineVoucherAirlineFeeToRedeemPerPerson, setAirlineVoucherAirlineFeeToRedeemPerPerson] = useState('')
  const [airlineVoucherPltFeeToRedeemPerPerson, setAirlineVoucherPltFeeToRedeemPerPerson] = useState('')

  useEffect(() => {
    if (frontContext) {
      const extractedBookingId = extractBookingId(frontContext)
      if (extractedBookingId) {
        setBookingId(extractedBookingId)
      } else {
        setBookingId('')
      }
    }
  }, [frontContext])

  const fetchBookingDetails = useCallback(async (id: string) => {
    if (!id.trim()) {
      setBookingDetails(null)
      return
    }

    setFetchingBooking(true)
    setErrorMessage('')

    try {
      const response = await fetch(`${config.apiBaseUrl}/bookings/${id}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch booking details')
      }

      const details: BookingDetails = await response.json()
      setBookingDetails(details)

      // Set currency from booking details, default to USD if not available
      if (details.currency) {
        setCurrency(details.currency)
      } else {
        setCurrency('USD')
      }

      // Prefill form fields based on booking details
      if (details.payment?.totalPaid) {
        setTotalPaidAmount(details.payment.totalPaid.toString())
      }

      // Set sales price from booking value
      // Auto-populate fields from booking details if available
      setSalesPrice(details.value.toString())

      // Prefill all form fields with booking details
      if (details.origin) {
        setChangeOrigin(details.origin)
        setOrigin(details.origin)
      }
      if (details.destination) {
        setChangeDestination(details.destination)
        setDestination(details.destination)
      }
      if (details.departureDate) {
        setChangeDepartDate(details.departureDate)
        setDepartDate(details.departureDate)
      }
      if (details.returnDate) {
        setChangeReturnDate(details.returnDate)
        setReturnDate(details.returnDate)
      }
      if (details.departureALTime) {
        // Extract just the time portion if it's a full datetime
        const timePart = details.departureALTime.includes('T')
          ? details.departureALTime.split('T')[1].substring(0, 5)
          : details.departureALTime
        setChangeDepartTime(timePart)
        setDepartTime(timePart)
      }
      if (details.returnALTime) {
        // Extract just the time portion if it's a full datetime
        const timePart = details.returnALTime.includes('T')
          ? details.returnALTime.split('T')[1].substring(0, 5)
          : details.returnALTime
        setChangeReturnTime(timePart)
        setReturnTime(timePart)
      }
      if (details.pnr) {
        setChangeRecordLocatorPnr(details.pnr)
      }
      if (details.airlineReference) {
        setChangeAirlineReferenceNo(details.airlineReference)
      }
      if (details.eticketNumber) {
        setChangeETicketNo(details.eticketNumber)
      }

      // Prefill voluntaryChange fields if available
      if (details.voluntaryChange) {
        if (details.voluntaryChange.vcCustomerChangeFeeCredFdPaid) {
          setChangeVcCustomerChangeFee(details.voluntaryChange.vcCustomerChangeFeeCredFdPaid.toString())
        }
        if (details.voluntaryChange.vcCustomerChangeFeeCredFdPaidDate) {
          setChangeVcCustomerChangeFeePaidDate(details.voluntaryChange.vcCustomerChangeFeeCredFdPaidDate)
        }
        if (details.voluntaryChange.vcSupplierChangeFeePaid) {
          setChangeVcSupplierChangeFee(details.voluntaryChange.vcSupplierChangeFeePaid.toString())
        }
        if (details.voluntaryChange.vcSupplierChangeFeePaidDate) {
          setChangeVcSupplierChangeFeePaidDate(details.voluntaryChange.vcSupplierChangeFeePaidDate)
        }
        if (details.voluntaryChange.vcPltCreditUpdatedPdFields) {
          // Map the text value to the corresponding dropdown value
          const fieldValue = details.voluntaryChange.vcPltCreditUpdatedPdFields
          if (fieldValue.includes('Yes Completed') && !fieldValue.includes('Method')) {
            setChangeVcPltCreditUpdatedFields('vc-yes-completed')
          } else if (fieldValue.includes('Not Required') || fieldValue.includes('Method 1')) {
            setChangeVcPltCreditUpdatedFields('plt-credit-not-required-method-1')
          } else if (fieldValue.includes('Yes Completed') && fieldValue.includes('Method 2')) {
            setChangeVcPltCreditUpdatedFields('plt-credit-yes-completed-method-2')
          } else {
            setChangeVcPltCreditUpdatedFields('vc-yes-completed') // Default value
          }
        }
      }

      // Calculate and set passengers count (excluding infants)
      if (details.travellersTotal !== undefined && details.travellersTotal !== null) {
        let infantsTotal = 0
        if (details.infantsTotal) {
          infantsTotal = parseInt(details.infantsTotal)
        }
        const passengersExcludingInfants = details.travellersTotal - infantsTotal
        setPassengersCount(passengersExcludingInfants.toString())
      }

      // Prefill cancellationAndRefund fields if available
      if (details.cancellationAndRefund) {
        if (
          details.cancellationAndRefund.xlLayByTerminationFee !== undefined &&
          details.cancellationAndRefund.xlLayByTerminationFee !== null
        ) {
          setXlLayByTerminationFee(details.cancellationAndRefund.xlLayByTerminationFee.toString())
        }
        if (details.cancellationAndRefund.xlCancellationDate) {
          setXlCancellationDate(details.cancellationAndRefund.xlCancellationDate)
        }
        if (details.cancellationAndRefund.xlPartialBookingCancellationDate) {
          setXlPartialBookingCancellationDate(details.cancellationAndRefund.xlPartialBookingCancellationDate)
        }
        if (details.cancellationAndRefund.xlPartialBookingEticketNumbers) {
          setXlPartialBookingEticketNumbers(details.cancellationAndRefund.xlPartialBookingEticketNumbers)
        }
        if (
          details.cancellationAndRefund.xlCustomerTotalPaidToDate !== undefined &&
          details.cancellationAndRefund.xlCustomerTotalPaidToDate !== null
        ) {
          setXlCustomerTotalPaidToDate(details.cancellationAndRefund.xlCustomerTotalPaidToDate.toString())
        }
        if (details.cancellationAndRefund.xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided) {
          setXlStripeSubscriptionCancelled(
            details.cancellationAndRefund.xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided.toLowerCase() === 'yes',
          )
        }
        if (details.cancellationAndRefund.xlFinalEmailSentToCustomer) {
          setXlFinalEmailSentToCustomer(details.cancellationAndRefund.xlFinalEmailSentToCustomer.toLowerCase())
        }
        if (details.cancellationAndRefund.xlCancelledWithSupplierRequestedRefund) {
          setXlCancelledWithSupplierRefund(
            details.cancellationAndRefund.xlCancelledWithSupplierRequestedRefund.toLowerCase(),
          )
        }
        if (
          details.cancellationAndRefund.rfSupplierTotalCashRefund !== undefined &&
          details.cancellationAndRefund.rfSupplierTotalCashRefund !== null
        ) {
          setRfSupplierTotalCashRefund(details.cancellationAndRefund.rfSupplierTotalCashRefund.toString())
        }
        if (details.cancellationAndRefund.rfSupplierExpectRefundBackDate) {
          setRfSupplierExpectRefundBackDate(details.cancellationAndRefund.rfSupplierExpectRefundBackDate)
        }
        if (details.cancellationAndRefund.rfCustomerAdvisedRefundWouldBeSentDate) {
          setRfCustomerAdvisedRefundDate(details.cancellationAndRefund.rfCustomerAdvisedRefundWouldBeSentDate)
        }
        if (
          details.cancellationAndRefund.refundAmountToCustomer !== undefined &&
          details.cancellationAndRefund.refundAmountToCustomer !== null
        ) {
          setRfAmountPromisedToCustomer(details.cancellationAndRefund.refundAmountToCustomer.toString())
        }
        if (
          details.cancellationAndRefund.cancellationReason &&
          details.cancellationAndRefund.cancellationReason !== 'Unknown'
        ) {
          setCancellationReason(details.cancellationAndRefund.cancellationReason)
        }
      }

      // Prefill cancelToCredit fields if available
      if (details.cancelToCredit) {
        if (details.cancelToCredit.airlineVoucherSentRequestToConvert) {
          setAirlineVoucherSentRequest(
            details.cancelToCredit.airlineVoucherSentRequestToConvert.toLowerCase() === 'yes' ? 'yes' : 'no',
          )
        }
        if (details.cancelToCredit.airlineVoucherRedeemBefore) {
          setAirlineVoucherRedeemBefore(details.cancelToCredit.airlineVoucherRedeemBefore)
        }
        if (details.cancelToCredit.airlineVoucherExpiration) {
          setAirlineVoucherExpiration(details.cancelToCredit.airlineVoucherExpiration)
        }
        if (details.cancelToCredit.airlineVoucherCustomerFullyPaid) {
          setAirlineVoucherCustomerFullyPaid(
            details.cancelToCredit.airlineVoucherCustomerFullyPaid.toLowerCase() === 'yes' ? 'yes' : 'no',
          )
        }
        if (details.cancelToCredit.airlineVoucherSendConfirmationEmail) {
          setAirlineVoucherSendConfirmationEmail(
            details.cancelToCredit.airlineVoucherSendConfirmationEmail.toLowerCase().includes('yes')
              ? 'yes-sent'
              : 'no',
          )
        }
        if (details.cancelToCredit.airlineVoucherEticketNumbersInCredit) {
          setAirlineVoucherEticketNumbersInCredit(details.cancelToCredit.airlineVoucherEticketNumbersInCredit)
        }
        if (
          details.cancelToCredit.airlineVoucherAirlineFeeToRedeemPerPerson !== undefined &&
          details.cancelToCredit.airlineVoucherAirlineFeeToRedeemPerPerson !== null
        ) {
          setAirlineVoucherAirlineFeeToRedeemPerPerson(
            details.cancelToCredit.airlineVoucherAirlineFeeToRedeemPerPerson.toString(),
          )
        }
        if (
          details.cancelToCredit.airlineVoucherPltFeeToRedeemPerPerson !== undefined &&
          details.cancelToCredit.airlineVoucherPltFeeToRedeemPerPerson !== null
        ) {
          setAirlineVoucherPltFeeToRedeemPerPerson(
            details.cancelToCredit.airlineVoucherPltFeeToRedeemPerPerson.toString(),
          )
        }
      }

      // Prefill creditRedemption fields if available
      if (details.creditRedemption) {
        // Set credit redemption fields (these fields are shared with voluntary change form)
        if (
          details.creditRedemption.vcCustomerChangeFeeCredFdPaid !== undefined &&
          details.creditRedemption.vcCustomerChangeFeeCredFdPaid !== null
        ) {
          setVcCustomerChangeFee(details.creditRedemption.vcCustomerChangeFeeCredFdPaid.toString())
        }
        if (details.creditRedemption.vcCustomerChangeFeeCredFdPaidDate) {
          setVcCustomerChangeFeePaidDate(details.creditRedemption.vcCustomerChangeFeeCredFdPaidDate)
        }
        if (
          details.creditRedemption.vcSupplierChangeFeePaid !== undefined &&
          details.creditRedemption.vcSupplierChangeFeePaid !== null
        ) {
          setVcSupplierChangeFee(details.creditRedemption.vcSupplierChangeFeePaid.toString())
        }
        if (details.creditRedemption.vcSupplierChangeFeePaidDate) {
          setVcSupplierChangeFeePaidDate(details.creditRedemption.vcSupplierChangeFeePaidDate)
        }
        if (details.creditRedemption.vcPltCreditUpdatedPdFields) {
          // Map the text value to the corresponding dropdown value
          const fieldValue = details.creditRedemption.vcPltCreditUpdatedPdFields
          if (fieldValue.includes('Yes Completed') && !fieldValue.includes('Method')) {
            setVcPltCreditUpdatedFields('vc-yes-completed')
          } else if (fieldValue.includes('Not Required') || fieldValue.includes('Method 1')) {
            setVcPltCreditUpdatedFields('plt-credit-not-required-method-1')
          } else if (fieldValue.includes('Yes Completed') && fieldValue.includes('Method 2')) {
            setVcPltCreditUpdatedFields('plt-credit-yes-completed-method-2')
          } else {
            setVcPltCreditUpdatedFields('vc-yes-completed') // Default value
          }
        }
        if (details.creditRedemption.airlineVoucherEticketNumbers) {
          setAirlineVoucherEticketNumbers(details.creditRedemption.airlineVoucherEticketNumbers)
        }
        if (details.creditRedemption.airlineVoucherAirlineReferenceNumber) {
          setAirlineVoucherAirlineReferenceNumber(details.creditRedemption.airlineVoucherAirlineReferenceNumber)
        }
        if (details.creditRedemption.airlineVoucherNewPnr) {
          setAirlineVoucherNewPnr(details.creditRedemption.airlineVoucherNewPnr)
        }
      }

      // Calculate supplier fee
      let supplierFeeValue = details.cancelationPenalty
      if (supplierFeeValue !== null && supplierFeeValue !== undefined) {
        setSupplierFee(supplierFeeValue.toString())
      } else {
        setSupplierFee('')
      }
    } catch (error) {
      console.error('Error fetching booking details:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to fetch booking details')
      setBookingDetails(null)
    } finally {
      setFetchingBooking(false)
    }
  }, [])

  const fetchRefundQuote = useCallback(async (id: string) => {
    setRefundQuoteLoading(true)
    setRefundQuoteError('')
    setRefundQuoteData(null)
    try {
      // No quote record: this screen never scores the quote, so a record would stay without a confidence level.
      const data = await retrieveRefundQuote(id, { record: false })
      setRefundQuoteData(data)

      // Fetch fare rules in the background and merge when ready
      fetch(`${config.apiBaseUrl}/bookings/${id}/fare-rules`, { credentials: 'include', cache: 'no-store' })
        .then(r => (r.ok ? r.json() : Promise.reject()))
        .then(fareData =>
          setRefundQuoteData(prev =>
            prev ? { ...prev, fareRules: fareData.fareRules, fareRulesError: fareData.fareRulesError } : prev,
          ),
        )
        .catch(() => setRefundQuoteData(prev => (prev ? { ...prev, fareRulesError: true } : prev)))
    } catch (error) {
      setRefundQuoteError(error instanceof Error ? error.message : 'Failed to fetch refund quote from GDS')
    } finally {
      setRefundQuoteLoading(false)
    }
  }, [])

  // Auto-populate calculator fields from GDS data once refund quote is fetched
  useEffect(() => {
    if (!refundQuoteData || !bookingDetails || refundQuoteData.airlineControl) return

    const depDate = bookingDetails.departureDate
    const isAfterDeparture = depDate ? new Date() >= new Date(depDate) : false
    const timing = isAfterDeparture ? 'AFTER_DEPARTURE' : 'BEFORE_DEPARTURE'

    if (selectedAction === 'refund-calculator') {
      const total = (refundQuoteData.tickets ?? []).reduce((sum, ticket) => {
        if (!ticket.isRefundable || !ticket.refundPenalties) return sum
        const penalty = ticket.refundPenalties.find(p => p.applicability === timing)
        const amount = parseFloat(penalty?.penalty?.amount ?? '0') || 0
        return sum + amount
      }, 0)
      setSupplierFee(total.toFixed(2))
    }

    if (selectedAction === 'change-calculator') {
      const firstChangeable = (refundQuoteData.tickets ?? []).find(t => t.isChangeable && t.exchangePenalties?.length)
      if (firstChangeable?.exchangePenalties) {
        const penalty = firstChangeable.exchangePenalties.find(p => p.applicability === timing)
        const amount = penalty?.penalty?.amount
        if (amount) setChangeFeePerPassenger(parseFloat(amount).toFixed(2))
      }
    }
  }, [refundQuoteData, bookingDetails, selectedAction])

  // Per-pax refund penalty map: { [ticketNumber]: penaltyAmount }
  const supplierRefundFee = useMemo<Record<string, number>>(() => {
    if (!refundQuoteData || !bookingDetails || refundQuoteData.airlineControl) return {}
    const depDate = bookingDetails.departureDate
    const isAfterDeparture = depDate ? new Date() >= new Date(depDate) : false
    const timing = isAfterDeparture ? 'AFTER_DEPARTURE' : 'BEFORE_DEPARTURE'
    return Object.fromEntries(
      (refundQuoteData.tickets ?? [])
        .filter(t => t.isRefundable && t.refundPenalties)
        .map(t => {
          const penalty = t.refundPenalties!.find(p => p.applicability === timing)
          return [t.number, parseFloat(penalty?.penalty?.amount ?? '0') || 0] as const
        }),
    )
  }, [refundQuoteData, bookingDetails])

  // Debounced booking ID fetch - for refund calculator, change QC, cancellation QC, and change calculator
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (bookingId.trim()) {
        fetchBookingDetails(bookingId.trim())
      } else {
        setBookingDetails(null)
      }
    }, 800)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [bookingId, selectedAction, fetchBookingDetails])

  const handleActionChange = (e: Event) => {
    if (submitting.current) return

    const target = e.target as HTMLSelectElement
    const newAction = target.value as FormAction
    setSelectedAction(newAction)

    // Fetch booking details if we have a booking ID
    if (bookingId.trim()) {
      fetchBookingDetails(bookingId.trim())
    } else {
      setBookingDetails(null)
      setCurrency('USD')
    }
  }

  const calculatePltFee = () => {
    const passengers = parseFloat(passengersCount) || 0
    const price = parseFloat(salesPrice) || 0

    if (passengers <= 0 || price <= 0) return 0

    return Math.min(150 * passengers, 0.1 * price)
  }

  const calculateConsolidatorFee = () => {
    const owningPcc = refundQuoteData?.owningPcc
    const ticketingPcc = refundQuoteData?.ticketingPcc
    if (!owningPcc || !ticketingPcc) return 0

    const rule = CONSOLIDATOR_REFUND_FEE[`${owningPcc} (${ticketingPcc})`]
    if (!rule) return 0

    const commission = Object.values(refundQuoteData?.ticketPayments ?? {}).reduce(
      (sum, p) => sum + (p.commission ?? 0),
      0,
    )
    return computeConsolidatorRefundFee(rule, {
      adtChdCount: parseFloat(passengersCount) || 0,
      infCount: parseInt(bookingDetails?.infantsTotal ?? '0') || 0,
      commission,
      gstRate: resolveDomesticGstRate(refundQuoteData?.fareRules),
    }).total
  }

  const calculateNetRefund = () => {
    const paid = parseFloat(totalPaidAmount) || 0
    const pltFeeValue = calculatePltFee()
    const supplierFeeValue = parseFloat(supplierFee) || 0
    const consolidatorFeeValue = calculateConsolidatorFee()

    const netRefund = paid - pltFeeValue - supplierFeeValue - consolidatorFeeValue
    return netRefund.toFixed(2)
  }

  const getNetRefundAmount = () => {
    return parseFloat(calculateNetRefund())
  }

  const calculateTotalChangeFee = () => {
    const passengers = parseFloat(passengersCount) || 0
    const changeFeePP = parseFloat(changeFeePerPassenger) || 0
    const fareDiff = parseFloat(fareDifference) || 0
    const pltFeePP = parseFloat(changeCalculatorConsolidatorFeePerPassenger) || 0

    return changeFeePP * passengers + fareDiff + pltFeePP * passengers
  }

  const clearSuccessTimer = () => {
    if (successTimer.current) clearTimeout(successTimer.current)
    successTimer.current = null
  }

  useEffect(() => clearSuccessTimer, [])

  const resetForm = () => {
    if (submitting.current) return

    clearSuccessTimer()
    setFormStatus('idle')
    setErrorMessage('')
    setTotalPaidAmount('')
    setSupplierFee('')
    setRefundQuoteData(null)
    setRefundQuoteError('')

    setPassengersCount('')
    setSalesPrice('')
    setChangeFeePerPassenger('')
    setFareDifference('')
    setChangeCalculatorConsolidatorFeePerPassenger('75')
    setCancellationReason('')
    setBookingDetails(null)
    setCopySuccess(false)
    setCurrency('USD')

    // Reset change form fields
    setChangeVcCustomerChangeFee('')
    setChangeVcCustomerChangeFeePaidDate('')
    setChangeVcSupplierChangeFee('')
    setChangeVcSupplierChangeFeePaidDate('')
    setChangeVcPltCreditUpdatedFields('vc-yes-completed')
    setChangeOrigin('')
    setChangeDestination('')
    setChangeDepartDate('')
    setChangeReturnDate('')
    setChangeDepartTime('')
    setChangeReturnTime('')
    setChangeRecordLocatorPnr('')
    setChangeAirlineReferenceNo('')
    setChangeETicketNo('')

    // Reset credit redemption form fields
    setVcCustomerChangeFee('')
    setVcCustomerChangeFeePaidDate('')
    setVcSupplierChangeFee('')
    setVcSupplierChangeFeePaidDate('')
    setVcPltCreditUpdatedFields('vc-yes-completed')
    setOrigin('')
    setDestination('')
    setDepartDate('')
    setReturnDate('')
    setDepartTime('')
    setReturnTime('')
    setAirlineVoucherNewPnr('')
    setAirlineVoucherAirlineReferenceNumber('')
    setAirlineVoucherEticketNumbers('')

    if (user) {
      const username = user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email
      const notePrefill = `updated by ${username} in Front`
      setChangeNote(notePrefill)
      setNote(notePrefill)
    } else {
      setChangeNote('')
      setNote('')
    }

    // Reset cancellation form fields
    setXlLayByTerminationFee('')
    setXlCancellationDate('')
    setXlCustomerTotalPaidToDate('')
    setXlStripeSubscriptionCancelled(false)
    setXlFinalEmailSentToCustomer('no')
    setXlCancelledWithSupplierRefund('yes')
    setRfSupplierTotalCashRefund('')
    setRfSupplierExpectRefundBackDate('')
    setRfAmountPromisedToCustomer('')
    setRfCustomerAdvisedRefundDate('')

    // Credit redemption form reuses change form fields, which are already reset

    // Reset cancel to credit form fields
    setAirlineVoucherSentRequest('yes')
    setAirlineVoucherRedeemBefore('')
    setAirlineVoucherExpiration('')
    setAirlineVoucherCustomerFullyPaid('yes')
    setAirlineVoucherSendConfirmationEmail('yes-sent')
    setAirlineVoucherEticketNumbersInCredit('')
    setAirlineVoucherAirlineFeeToRedeemPerPerson('')
    setAirlineVoucherPltFeeToRedeemPerPerson('')

    // Reset change QC form fields
    setVcQcComplete('yes')
  }

  const copyQuoteToClipboard = async () => {
    let quote = ''

    if (selectedAction === 'refund-calculator') {
      const pltFeeValue = calculatePltFee()
      const supplierFeeValue = parseFloat(supplierFee) || 0
      const totalPaid = parseFloat(totalPaidAmount) || 0
      const netRefund = getNetRefundAmount()

      const consolidatorFeeValue = calculateConsolidatorFee()
      quote = `CANCELLATION REFUND QUOTE

Booking ID: ${bookingId}
Sales Price: ${currency} ${parseFloat(salesPrice).toFixed(2)}
Passengers (excl. infants): ${passengersCount}

REASON FOR CANCELLATION:
${cancellationReason}

PAYMENT BREAKDOWN:
Total Amount Paid: ${currency} ${totalPaid.toFixed(2)}
Airline Cancellation Penalty: -${currency} ${supplierFeeValue.toFixed(2)}${
        consolidatorFeeValue > 0
          ? `
Consolidator Refund Fee: -${currency} ${consolidatorFeeValue.toFixed(2)}`
          : ''
      }
PLT Termination Fee: -${currency} ${pltFeeValue.toFixed(2)}

${netRefund < 0 ? 'SHORTFALL' : 'NET REFUND'}: ${currency} ${Math.abs(netRefund).toFixed(2)}

Generated on: ${new Date().toLocaleString()}`
    } else if (selectedAction === 'change-calculator') {
      const passengers = parseFloat(passengersCount) || 0
      const changeFeePP = parseFloat(changeFeePerPassenger) || 0
      const fareDiff = parseFloat(fareDifference) || 0
      const consolidatorFeePP = parseFloat(changeCalculatorConsolidatorFeePerPassenger) || 0
      const totalChangeFee = calculateTotalChangeFee()

      quote = `FLIGHT CHANGE CALCULATOR

Booking ID: ${bookingId}
Passengers (excl. infants): ${passengersCount}

CHANGE FEE BREAKDOWN:
Airline Change Fee: ${currency} ${(changeFeePP * passengers).toFixed(2)} (${currency} ${changeFeePP.toFixed(2)} per passenger)
Fare Difference: ${currency} ${fareDiff.toFixed(2)}
Consolidator Fee: ${currency} ${(consolidatorFeePP * passengers).toFixed(2)} (${currency} ${consolidatorFeePP.toFixed(2)} per passenger)

TOTAL AMOUNT DUE (For Entire Booking): ${currency} ${totalChangeFee.toFixed(2)}

Generated on: ${new Date().toLocaleString()}`
    }

    try {
      await navigator.clipboard.writeText(quote)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
    }
  }

  const handleFormSubmit = async (action: FormAction, e: Event) => {
    e.preventDefault()

    if (submitting.current) return

    if (!bookingId.trim()) {
      setErrorMessage('Booking ID is required')
      setFormStatus('error')
      return
    }

    submitting.current = true
    clearSuccessTimer()
    setFormStatus('loading')
    setErrorMessage('')

    try {
      let requestBody: any = {}

      if (action === 'change') {
        requestBody = {
          'vc-customer-change-fee-plt-credit-fd-paid': parseFloat(changeVcCustomerChangeFee) || 0,
          'vc-customer-change-fee-plt-credit-fd-paid-date': changeVcCustomerChangeFeePaidDate,
          'vc-supplier-change-fee-paid': parseFloat(changeVcSupplierChangeFee) || 0,
          'vc-supplier-change-fee-paid-date': changeVcSupplierChangeFeePaidDate,
          'vc-send-updated-trip-confirmation': 'yes',
          'vc-plt-credit-updated-pd-fields': changeVcPltCreditUpdatedFields,
          origin: changeOrigin,
          destination: changeDestination,
          'depart-date': changeDepartDate,
          'depart-al-time':
            changeDepartDate && changeDepartTime ? `${changeDepartDate}T${changeDepartTime}:00` : changeDepartTime,
          'record-locator-pnr': changeRecordLocatorPnr,
          'airline-reference-no': changeAirlineReferenceNo,
          'e-ticket-no': changeETicketNo,
          note: changeNote,
          ...(changeReturnDate !== '' && changeReturnDate !== undefined && changeReturnDate !== null
            ? {
                'return-date': changeReturnDate,
                'return-al-time':
                  changeReturnDate && changeReturnTime
                    ? `${changeReturnDate}T${changeReturnTime}:00`
                    : changeReturnTime,
              }
            : {}),
        }
      } else if (action === 'cancellation') {
        requestBody = {
          'xl-lay-by-termination-fee': parseFloat(xlLayByTerminationFee) || 0,
          'xl-cancellation-date': xlCancellationDate === '' ? null : xlCancellationDate,
          'xl-partial-booking-cancellation-date':
            xlPartialBookingCancellationDate === '' ? null : xlPartialBookingCancellationDate,
          'xl-partial-booking-eticket-numbers': xlPartialBookingEticketNumbers,
          'xl-customer-total-paid-to-date': parseFloat(xlCustomerTotalPaidToDate) || 0,
          'xl-stripe-subscription-cancelled-and-unpaid-invoices-voided': xlStripeSubscriptionCancelled,
          'xl-final-email-sent-to-customer': xlFinalEmailSentToCustomer,
          'xl-cancelled-with-supplier-requested-refund': xlCancelledWithSupplierRefund,
          'rf-supplier-total-cash-refund': parseFloat(rfSupplierTotalCashRefund) || 0,
          'rf-supplier-expect-refund-back-date': rfSupplierExpectRefundBackDate,
          'rf-amount-promised-to-customer': parseFloat(rfAmountPromisedToCustomer) || 0,
          'rf-customer-advised-refund-would-be-sent-date':
            rfCustomerAdvisedRefundDate === '' ? null : rfCustomerAdvisedRefundDate,
          'cancellation-reason': cancellationReason || null,
          note,
        }
      } else if (action === 'credit-redemption') {
        requestBody = {
          'vc-customer-change-fee-plt-credit-fd-paid': parseFloat(vcCustomerChangeFee) || 0,
          'vc-customer-change-fee-plt-credit-fd-paid-date': vcCustomerChangeFeePaidDate,
          'vc-supplier-change-fee-paid': parseFloat(vcSupplierChangeFee) || 0,
          'vc-supplier-change-fee-paid-date': vcSupplierChangeFeePaidDate,
          'vc-plt-credit-updated-pd-fields': vcPltCreditUpdatedFields,
          origin,
          destination,
          'depart-date': departDate,
          'depart-al-time': departDate && departTime ? `${departDate}T${departTime}:00` : departTime,
          'return-date': returnDate === '' ? null : returnDate,
          'return-al-time':
            returnDate === '' ? null : returnDate && returnTime ? `${returnDate}T${returnTime}:00` : returnTime,
          'record-locator-pnr': airlineVoucherNewPnr,
          'airline-reference-no': airlineVoucherAirlineReferenceNumber,
          'e-ticket-no': airlineVoucherEticketNumbers,
          note,
        }
      } else if (action === 'cancel-to-credit') {
        requestBody = {
          'airline-voucher-sent-request-to-convert': airlineVoucherSentRequest,
          'airline-voucher-redeem-before': airlineVoucherRedeemBefore,
          'airline-voucher-expiration': airlineVoucherExpiration,
          'airline-voucher-customer-fully-paid': airlineVoucherCustomerFullyPaid,
          'airline-voucher-send-confirmation-email': airlineVoucherSendConfirmationEmail,
          'airline-voucher-eticket-numbers-in-credit': airlineVoucherEticketNumbersInCredit,
          'airline-voucher-airline-fee-to-redeem-per-person':
            parseFloat(airlineVoucherAirlineFeeToRedeemPerPerson) || 0,
          'airline-voucher-plt-fee-to-redeem-per-person': parseFloat(airlineVoucherPltFeeToRedeemPerPerson) || 0,
          note,
        }
      } else if (action === 'change-qc') {
        requestBody = {
          'vc-qc-complete': vcQcComplete,
          note: changeNote,
        }
      } else if (action === 'cancellation-qc') {
        requestBody = {
          note,
        }
      }

      const actionToEndpointMap: Record<string, string> = {
        cancellation: 'cancel',
        'credit-redemption': 'redeem-credit',
        'cancel-to-credit': 'cancel-to-credit',
        'change-qc': 'quality-check-change',
        'cancellation-qc': 'quality-check-cancellation',
      }

      const endpoint = actionToEndpointMap[action] || action

      const response = await fetch(`${config.apiBaseUrl}/bookings/${bookingId.trim()}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to submit form')
      }

      setFormStatus('success')
      successTimer.current = setTimeout(() => {
        successTimer.current = null
        setFormStatus('idle')
      }, 3000)
    } catch (error) {
      console.error('Error submitting form:', error)
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit form')
      setFormStatus('error')
    } finally {
      submitting.current = false
    }
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4 flex items-center">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clipRule="evenodd"
            />
          </svg>
          Dashboard
        </a>
        <span className="mx-2 text-gray-500">/</span>
        <span className="text-gray-700">Change & Cancellation</span>
      </div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Change & Cancellation</h1>

      {isFrontApp && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-blue-700">
            Connected to Front conversation. Booking IDs will be automatically detected when available.
          </p>
        </div>
      )}

      <div className="mb-6">
        <label
          htmlFor="bookingId"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Booking ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="bookingId"
            type="text"
            value={bookingId}
            onChange={e => setBookingId((e.target as HTMLInputElement).value)}
            disabled={formStatus === 'loading'}
            className={`w-full px-3 py-2 border ${formStatus === 'error' && !bookingId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500`}
            placeholder="Enter Booking ID"
            required
          />
          {fetchingBooking && (
            <div className="absolute right-3 top-2">
              <svg
                className="animate-spin h-5 w-5 text-indigo-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
          )}
        </div>
        {formStatus === 'error' && !bookingId && <p className="mt-1 text-sm text-red-600">Booking ID is required</p>}
        {errorMessage && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
        {bookingId && isFrontApp && (
          <p className="mt-1 text-xs text-gray-500">Booking ID auto-detected from Front conversation</p>
        )}
        {bookingDetails &&
          (selectedAction === 'refund-calculator' ||
            selectedAction === 'change-qc' ||
            selectedAction === 'cancellation-qc' ||
            selectedAction === 'change-calculator') && (
            <p className="mt-1 text-xs text-green-600">Booking details loaded successfully</p>
          )}
        {fetchingBooking &&
          (selectedAction === 'refund-calculator' ||
            selectedAction === 'change-qc' ||
            selectedAction === 'cancellation-qc' ||
            selectedAction === 'change-calculator') && (
            <p className="mt-1 text-xs text-blue-600">Loading booking details...</p>
          )}
      </div>

      <div className="mb-6">
        <label
          htmlFor="action"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Select Action
        </label>
        <select
          id="action"
          value={selectedAction}
          onChange={handleActionChange}
          disabled={formStatus === 'loading'}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        >
          <option value="change">Change</option>
          <option value="change-qc">Change QC</option>
          <option value="cancellation">Cancellation</option>
          <option value="cancellation-qc">Cancellation QC</option>
          <option value="credit-redemption">Credit Redemption</option>
          <option value="cancel-to-credit">Cancel To Credit</option>
          <option value="refund-calculator">Refund Calculator</option>
          <option value="change-calculator">Change Calculator</option>
        </select>
      </div>

      {selectedAction === 'refund-calculator' && (
        <div className="space-y-4">
          <div>
            <button
              type="button"
              onClick={() => fetchRefundQuote(bookingId.trim())}
              disabled={!bookingId.trim() || refundQuoteLoading}
              className="w-full px-4 py-2 bg-teal-600 text-white font-medium rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {refundQuoteLoading ? 'Pulling from GDS...' : 'Retrieve Refund Quote'}
            </button>
            {refundQuoteLoading && <QuoteLoadingPanel />}
            {refundQuoteError && <p className="mt-1 text-sm text-red-600">{refundQuoteError}</p>}
          </div>

          {refundQuoteData?.airlineControl && (
            <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
              <p className="text-sm font-semibold text-amber-800">Flight is under airline control</p>
              <p className="text-sm text-amber-700 mt-1">Cancellations must be done directly with the airline.</p>
            </div>
          )}

          {refundQuoteData?.partialData && !refundQuoteData.airlineControl && (
            <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
              <p className="text-sm text-yellow-800">
                Partial / Incomplete data retrieved — some ticket conditions may be missing or inaccurate.
              </p>
            </div>
          )}

          {refundQuoteData && !refundQuoteData.airlineControl && (
            <div className="border border-gray-200 rounded-md bg-gray-50 overflow-hidden">
              <button
                type="button"
                onClick={() => setRefundConditionsExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                <span className="flex items-center gap-2">
                  GDS Refund Conditions
                  {bookingDetails?.departureDate && (
                    <span
                      className={`text-xs font-normal px-1.5 py-0.5 rounded ${new Date() >= new Date(bookingDetails.departureDate) ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}
                    >
                      {new Date() >= new Date(bookingDetails.departureDate) ? 'After departure' : 'Before departure'}
                    </span>
                  )}
                </span>
                <span className="text-gray-400">{refundConditionsExpanded ? '▲' : '▼'}</span>
              </button>
              {refundConditionsExpanded && (
                <div className="px-3 pb-3 space-y-4">
                  {(refundQuoteData.tickets ?? []).map(ticket => (
                    <div
                      key={ticket.number}
                      className="border border-gray-200 rounded p-3 bg-white space-y-2"
                    >
                      <p className="text-xs font-mono text-gray-500">
                        Ticket: {ticket.number}
                        {refundQuoteData.travelersByTicket?.[ticket.number] && (
                          <span className="ml-2 text-gray-700 not-italic font-sans">
                            — {refundQuoteData.travelersByTicket[ticket.number]}
                          </span>
                        )}
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <span className="text-gray-500">Refundable</span>
                        <span
                          className={`text-right font-medium ${ticket.isRefundable ? (parseFloat(ticket.refundTotals?.subtotal ?? '1') === 0 ? 'text-amber-600' : 'text-green-700') : 'text-red-700'}`}
                        >
                          {ticket.isRefundable
                            ? parseFloat(ticket.refundTotals?.subtotal ?? '1') === 0
                              ? 'Taxes only'
                              : 'YES'
                            : 'NO'}
                        </span>
                        {ticket.isRefundable && (
                          <>
                            <span className="text-gray-500">Auto RF</span>
                            <span
                              className={`text-right ${ticket.isAutomatedRefundsEligible ? 'text-green-700 font-medium' : 'text-gray-700'}`}
                            >
                              {ticket.isAutomatedRefundsEligible ? 'YES' : 'NO'}
                            </span>
                          </>
                        )}
                      </div>

                      {ticket.isRefundable && ticket.refundPenalties && ticket.refundPenalties.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-600">Penalties</p>
                          {ticket.refundPenalties.map((p, i) => (
                            <div
                              key={i}
                              className="grid grid-cols-2 gap-x-4 text-sm"
                            >
                              <span className="text-gray-500 text-xs">
                                {p.applicability === 'BEFORE_DEPARTURE' ? 'Before dep' : 'After dep'}
                                {p.source ? ` (${p.source})` : ''}
                              </span>
                              <span className="text-gray-800 text-right">
                                {p.penalty ? `${p.penalty.amount} ${p.penalty.currencyCode}` : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {(() => {
                        const allTaxes = refundQuoteData.allTaxesByTicket?.[ticket.number]
                        const refundableCodes = new Set(ticket.refundTaxes?.map(t => t.taxCode) ?? [])
                        if (allTaxes && allTaxes.length > 0) {
                          return (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Taxes</p>
                              <table className="w-full text-xs">
                                <tbody>
                                  {allTaxes.map(tax => {
                                    const color = refundableCodes.has(tax.taxCode) ? 'text-green-600' : 'text-red-500'
                                    return (
                                      <tr key={tax.taxCode}>
                                        <td className={`${color} pr-4 py-0.5`}>{tax.taxCode}</td>
                                        <td className={`${color} text-right`}>
                                          {tax.amount} {tax.currencyCode}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )
                        }
                        if (ticket.isRefundable && ticket.refundTaxes && ticket.refundTaxes.length > 0) {
                          return (
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Taxes</p>
                              <table className="w-full text-xs">
                                <tbody>
                                  {ticket.refundTaxes.map(tax => (
                                    <tr key={tax.taxCode}>
                                      <td className="text-green-600 pr-4 py-0.5">{tax.taxCode}</td>
                                      <td className="text-green-600 text-right">{tax.amount}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )
                        }
                        return null
                      })()}

                      {ticket.isRefundable && ticket.refundTotals && (
                        <div className="border-t border-gray-200 pt-2 space-y-1 text-sm">
                          <div className="grid grid-cols-2 gap-x-4">
                            <span className="text-gray-500">Total Refundable Base Fare</span>
                            <span className="text-gray-800 text-right">
                              {ticket.refundTotals.subtotal} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4">
                            <span className="text-gray-500">Taxes</span>
                            <span className="text-gray-800 text-right">
                              {ticket.refundTotals.taxes} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4">
                            <span className="text-gray-500">Fees</span>
                            <span className="text-gray-800 text-right">
                              {ticket.refundTotals.fees} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 font-semibold">
                            <span className="text-gray-700">Total</span>
                            <span className="text-gray-900 text-right">
                              {ticket.refundTotals.total} {ticket.refundTotals.currencyCode}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {(() => {
                    const depDate = bookingDetails?.departureDate
                    const isAfter = depDate ? new Date() >= new Date(depDate) : false
                    const timing = isAfter ? 'AFTER_DEPARTURE' : 'BEFORE_DEPARTURE'

                    const refundableTickets = (refundQuoteData.tickets ?? []).filter(
                      t => t.isRefundable && t.refundTotals,
                    )
                    if (refundableTickets.length < 2) return null

                    const currency = refundableTickets[0].refundTotals!.currencyCode
                    const totalPenalty = (refundQuoteData.tickets ?? []).reduce((sum, t) => {
                      if (!t.isRefundable || !t.refundPenalties) return sum
                      const p = t.refundPenalties.find(rp => rp.applicability === timing)
                      return sum + (parseFloat(p?.penalty?.amount ?? '0') || 0)
                    }, 0)

                    const totalSubtotal = refundableTickets.reduce(
                      (sum, t) => sum + (parseFloat(t.refundTotals!.subtotal) || 0),
                      0,
                    )
                    const totalTaxes = refundableTickets.reduce(
                      (sum, t) => sum + (parseFloat(t.refundTotals!.taxes) || 0),
                      0,
                    )
                    const totalFees = refundableTickets.reduce(
                      (sum, t) => sum + (parseFloat(t.refundTotals!.fees ?? '0') || 0),
                      0,
                    )
                    const totalRefund = refundableTickets.reduce(
                      (sum, t) => sum + (parseFloat(t.refundTotals!.total) || 0),
                      0,
                    )

                    return (
                      <div className="mt-2 border border-indigo-200 rounded p-3 bg-indigo-50 space-y-1 text-sm">
                        <p className="text-xs font-semibold text-indigo-700 mb-2">
                          Booking Total ({refundableTickets.length} tickets)
                        </p>
                        <div className="grid grid-cols-2 gap-x-4">
                          <span className="text-gray-500">Total Penalty</span>
                          <span className="text-gray-800 text-right">
                            {totalPenalty.toFixed(2)} {currency}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4">
                          <span className="text-gray-500">Subtotal</span>
                          <span className="text-gray-800 text-right">
                            {totalSubtotal.toFixed(2)} {currency}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4">
                          <span className="text-gray-500">Taxes</span>
                          <span className="text-gray-800 text-right">
                            {totalTaxes.toFixed(2)} {currency}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4">
                          <span className="text-gray-500">Fees</span>
                          <span className="text-gray-800 text-right">
                            {totalFees.toFixed(2)} {currency}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 font-semibold border-t border-indigo-200 pt-1 mt-1">
                          <span className="text-indigo-700">Total Refundable</span>
                          <span className="text-indigo-900 text-right">
                            {totalRefund.toFixed(2)} {currency}
                          </span>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              <button
                type="button"
                onClick={() => setFareRulesExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 border-t border-gray-200"
              >
                <span>GDS Fare Rules</span>
                <span className="text-gray-400">{fareRulesExpanded ? '▲' : '▼'}</span>
              </button>
              {fareRulesExpanded && (
                <div className="px-3 pb-3 space-y-3">
                  {refundQuoteData.fareRulesError && (
                    <p className="text-xs text-red-500">Failed to load fare rules from GDS.</p>
                  )}
                  {refundQuoteData.fareRules && refundQuoteData.fareRules.length > 0
                    ? refundQuoteData.fareRules.map((fc, fcIdx) => (
                        <div
                          key={fcIdx}
                          className="border border-gray-200 rounded bg-white"
                        >
                          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                            <span className="text-xs font-semibold text-gray-700">{fc.originDestination}</span>
                            <span className="ml-2 text-xs text-gray-500">{fc.fareBasis}</span>
                            <span className="ml-2 text-xs text-gray-400">{fc.airline}</span>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {fc.rules.map((rule, rIdx) => (
                              <details
                                key={rIdx}
                                className="group"
                              >
                                <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50">
                                  <span>
                                    {rule.title}
                                    {rule.category ? (
                                      <span className="ml-1 text-gray-400">(Cat {rule.category})</span>
                                    ) : null}
                                  </span>
                                  <span className="text-gray-400 group-open:rotate-90 transition-transform">▶</span>
                                </summary>
                                <pre className="px-3 pb-3 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                  {rule.text}
                                </pre>
                              </details>
                            ))}
                          </div>
                        </div>
                      ))
                    : !refundQuoteData.fareRulesError && (
                        <p className="text-xs text-gray-400">No fare rules available.</p>
                      )}
                </div>
              )}

              <button
                type="button"
                onClick={() => setRefundTotalsExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 border-t border-gray-200"
              >
                <span>GDS Refund Totals</span>
                <span className="text-gray-400">{refundTotalsExpanded ? '▲' : '▼'}</span>
              </button>
              {refundTotalsExpanded &&
                (() => {
                  const totalPenalty = Object.values(supplierRefundFee).reduce((s, v) => s + v, 0)
                  const totalSubtotal = (refundQuoteData.tickets ?? []).reduce(
                    (s, t) => s + (parseFloat(t.refundTotals?.subtotal ?? '0') || 0),
                    0,
                  )
                  const totalTaxes = (refundQuoteData.tickets ?? []).reduce(
                    (s, t) => s + (parseFloat(t.refundTotals?.taxes ?? '0') || 0),
                    0,
                  )
                  const totalRFValue = (refundQuoteData.tickets ?? []).reduce(
                    (s, t) => s + (parseFloat(t.refundTotals?.total ?? '0') || 0),
                    0,
                  )
                  return (
                    <div className="px-3 py-3 space-y-1 text-sm">
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Residual Base Fare</span>
                        <span className="text-gray-800 text-right">
                          {currency} {totalSubtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Cancellation Penalty</span>
                        <span className="text-gray-800 text-right">
                          {currency} {totalPenalty.toFixed(2)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Refundable Taxes</span>
                        <span className="text-gray-800 text-right">
                          {currency} {totalTaxes.toFixed(2)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 font-semibold border-t border-gray-200 pt-1 mt-1">
                        <span className="text-green-700">RF Value (Total Refundable)</span>
                        <span className="text-green-900 text-right">
                          {currency} {totalRFValue.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )
                })()}
            </div>
          )}

          {bookingDetails && (
            <div className="mt-4 rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                <span className="text-sm font-semibold text-gray-700">Financial Summary</span>
              </div>
              <div className="px-3 py-3 space-y-1 text-sm">
                <div className="grid grid-cols-2 gap-x-4">
                  <span className="text-gray-500">Plan Total (PipeDrive)</span>
                  <span className="text-gray-800 text-right tabular-nums">
                    {parseFloat(salesPrice || '0').toFixed(2)} {currency}
                  </span>
                </div>
                {bookingDetails.payment?.deposit != null && bookingDetails.payment.deposit > 0 && (
                  <div className="grid grid-cols-2 gap-x-4">
                    <span className="text-gray-500">Deposit</span>
                    <span className="text-gray-800 text-right tabular-nums">
                      {bookingDetails.payment.deposit.toFixed(2)} {currency}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-4">
                  <span className="text-gray-500">Amount Paid (Chappie)</span>
                  <span className="text-gray-800 text-right tabular-nums">
                    {parseFloat(totalPaidAmount || '0').toFixed(2)} {currency}
                  </span>
                </div>
                {bookingDetails.payment?.pastDue != null && bookingDetails.payment.pastDue > 0 && (
                  <div className="grid grid-cols-2 gap-x-4">
                    <span className="text-orange-600">Past Due</span>
                    <span className="text-orange-700 text-right tabular-nums">
                      {bookingDetails.payment.pastDue.toFixed(2)} {currency}
                    </span>
                  </div>
                )}
                {bookingDetails.payment?.discountAmount != null && bookingDetails.payment.discountAmount > 0 && (
                  <div className="grid grid-cols-2 gap-x-4">
                    <span className="text-gray-500">Discount</span>
                    <span className="text-gray-800 text-right tabular-nums">
                      - {bookingDetails.payment.discountAmount.toFixed(2)} {currency}
                    </span>
                  </div>
                )}
                {supplierFee && (
                  <>
                    <div className="grid grid-cols-2 gap-x-4">
                      <span className="text-gray-500">Airline Cancellation Penalty</span>
                      <span className="text-gray-800 text-right tabular-nums">
                        {parseFloat(supplierFee || '0').toFixed(2)} {currency}
                      </span>
                    </div>
                    {calculateConsolidatorFee() > 0 && (
                      <div className="grid grid-cols-2 gap-x-4">
                        <span className="text-gray-500">Consolidator Refund Fee</span>
                        <span className="text-gray-800 text-right tabular-nums">
                          {calculateConsolidatorFee().toFixed(2)} {currency}
                        </span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-x-4">
                      <span className="text-gray-500">PLT Termination Fee</span>
                      <span className="text-gray-800 text-right tabular-nums">
                        {calculatePltFee().toFixed(2)} {currency}
                      </span>
                    </div>
                    <div
                      className={`grid grid-cols-2 gap-x-4 font-semibold border-t border-gray-200 pt-1 mt-1 ${getNetRefundAmount() < 0 ? 'text-red-700' : 'text-green-700'}`}
                    >
                      <span>Net Refundable</span>
                      <span className="text-right tabular-nums">
                        {getNetRefundAmount() < 0 ? '(Shortfall) ' : ''}
                        {Math.abs(getNetRefundAmount()).toFixed(2)} {currency}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {!supplierFee ? (
            <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400">
              <p className="text-yellow-700">{missingSupplierFeeMessage(bookingDetails)}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="salesPrice"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Sales Price ({currency})
                </label>
                <input
                  id="salesPrice"
                  type="number"
                  step="0.01"
                  value={salesPrice}
                  onChange={e => setSalesPrice((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label
                  htmlFor="passengersCount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Enter number of passengers (excluding infants)
                </label>
                <input
                  id="passengersCount"
                  type="number"
                  value={passengersCount}
                  onChange={e => setPassengersCount((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0"
                  min="0"
                />
              </div>
              <div>
                <label
                  htmlFor="totalPaidAmount"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Total Amount Paid by Customer ({currency}){' '}
                  <span className="text-sm font-normal text-gray-500">Manual invoices are not included</span>
                </label>
                <input
                  id="totalPaidAmount"
                  type="number"
                  step="0.01"
                  value={totalPaidAmount}
                  onChange={e => setTotalPaidAmount((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label
                  htmlFor="supplierFee"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Supplier Fee ({currency})
                </label>
                <input
                  id="supplierFee"
                  type="number"
                  step="0.01"
                  value={supplierFee}
                  onChange={e => setSupplierFee((e.target as HTMLInputElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label
                  htmlFor="pltFee"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  PLT Fee ({currency}){' '}
                  <span className="text-sm font-normal text-gray-500">(min(150 × passengers, 10% × sales price))</span>
                </label>
                <div className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-800">
                  {currency} {calculatePltFee().toFixed(2)}
                </div>
              </div>
              <div>
                <label
                  htmlFor="netRefund"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Net Refund Amount{' '}
                  <span className="text-sm font-normal text-gray-500">(total paid - PLT fee - supplier fee)</span>
                </label>
                <div
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg font-semibold ${
                    getNetRefundAmount() < 0 ? 'bg-red-50 text-red-800 border-red-200' : 'bg-gray-50 text-gray-800'
                  }`}
                >
                  {getNetRefundAmount() < 0 ? 'Shortfall: ' : ''}
                  {currency} {Math.abs(getNetRefundAmount()).toFixed(2)}
                </div>
              </div>

              <div>
                <label
                  htmlFor="cancellationReason"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Reason for Cancellation
                </label>
                <select
                  id="cancellationReason"
                  value={cancellationReason}
                  onChange={e => setCancellationReason((e.target as HTMLSelectElement).value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a reason</option>
                  <option value="Within 24 hours">Within 24 hours</option>
                  <option value="Voluntary">Voluntary</option>
                  <option value="Due to involuntary airline schedule change">
                    Due to involuntary airline schedule change
                  </option>
                  <option value="Default">Default</option>
                  <option value="Hardship Policy">Hardship Policy</option>
                  <option value="Fraud related">Fraud related</option>
                  <option value="PLT Credit - transferring to another booking">
                    PLT Credit - transferring to another booking
                  </option>
                  <option value="Technical Glitch">Technical Glitch</option>
                  <option value="Previous history with PLT">Previous history with PLT</option>
                  <option value="Did not pass fraud check">Did not pass fraud check</option>
                  <option value="Due to Corona Virus">Due to Corona Virus</option>
                  <option value="Testing">Testing</option>
                </select>
              </div>

              <div className="pt-4">
                <button
                  type="button"
                  onClick={copyQuoteToClipboard}
                  disabled={!bookingId || !salesPrice || !passengersCount || !totalPaidAmount || !cancellationReason}
                  className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {copySuccess ? (
                    <>
                      <svg
                        className="h-4 w-4 mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Copied to Clipboard!
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-4 w-4 mr-2"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                        />
                      </svg>
                      Copy Quote to Clipboard
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedAction === 'change-calculator' && (
        <div className="space-y-4">
          <div>
            <button
              type="button"
              onClick={() => fetchRefundQuote(bookingId.trim())}
              disabled={!bookingId.trim() || refundQuoteLoading}
              className="w-full px-4 py-2 bg-teal-600 text-white font-medium rounded-md shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {refundQuoteLoading ? 'Pulling from GDS...' : 'Retrieve Change Quote'}
            </button>
            {refundQuoteLoading && <QuoteLoadingPanel />}
            {refundQuoteError && <p className="mt-1 text-sm text-red-600">{refundQuoteError}</p>}
          </div>

          {refundQuoteData?.airlineControl && (
            <div className="mt-3 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
              <p className="text-sm font-semibold text-amber-800">Flight is under airline control</p>
              <p className="text-sm text-amber-700 mt-1">Cancellations must be done directly with the airline.</p>
            </div>
          )}

          {refundQuoteData?.partialData && !refundQuoteData.airlineControl && (
            <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-md">
              <p className="text-sm text-yellow-800">
                Partial / Incomplete data retrieved — some ticket conditions may be missing or inaccurate.
              </p>
            </div>
          )}

          {refundQuoteData && !refundQuoteData.airlineControl && (
            <div className="border border-gray-200 rounded-md bg-gray-50 overflow-hidden">
              <button
                type="button"
                onClick={() => setChangeConditionsExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              >
                <span className="flex items-center gap-2">
                  GDS Change Conditions
                  {bookingDetails?.departureDate && (
                    <span
                      className={`text-xs font-normal px-1.5 py-0.5 rounded ${new Date() >= new Date(bookingDetails.departureDate) ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}
                    >
                      {new Date() >= new Date(bookingDetails.departureDate) ? 'After departure' : 'Before departure'}
                    </span>
                  )}
                </span>
                <span className="text-gray-400">{changeConditionsExpanded ? '▲' : '▼'}</span>
              </button>
              {changeConditionsExpanded && (
                <div className="px-3 pb-3 space-y-4">
                  {(refundQuoteData.tickets ?? []).map(ticket => (
                    <div
                      key={ticket.number}
                      className="border border-gray-200 rounded p-3 bg-white space-y-2"
                    >
                      <p className="text-xs font-mono text-gray-500">
                        Ticket: {ticket.number}
                        {refundQuoteData.travelersByTicket?.[ticket.number] && (
                          <span className="ml-2 text-gray-700 not-italic font-sans">
                            — {refundQuoteData.travelersByTicket[ticket.number]}
                          </span>
                        )}
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <span className="text-gray-500">Changeable</span>
                        <span
                          className={`text-right ${ticket.isChangeable ? 'text-green-700 font-medium' : 'text-red-700 font-medium'}`}
                        >
                          {ticket.isChangeable ? 'YES' : 'NO'}
                        </span>
                      </div>
                      {ticket.isChangeable && ticket.exchangePenalties && ticket.exchangePenalties.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs font-medium text-gray-600">Penalties</p>
                          {ticket.exchangePenalties.map((p, i) => (
                            <div
                              key={i}
                              className="grid grid-cols-2 gap-x-4 text-sm"
                            >
                              <span className="text-gray-500 text-xs">
                                {p.applicability === 'BEFORE_DEPARTURE' ? 'Before dep' : 'After dep'}
                                {p.source ? ` (${p.source})` : ''}
                              </span>
                              <span className="text-gray-800 text-right">
                                {p.penalty ? `${p.penalty.amount} ${p.penalty.currencyCode}` : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {(() => {
                    const depDate = bookingDetails?.departureDate
                    const isAfter = depDate ? new Date() >= new Date(depDate) : false
                    const timing = isAfter ? 'AFTER_DEPARTURE' : 'BEFORE_DEPARTURE'
                    const changeableTickets = (refundQuoteData.tickets ?? []).filter(
                      t => t.isChangeable && t.exchangePenalties?.length,
                    )
                    if (changeableTickets.length < 2) return null
                    const currency =
                      changeableTickets[0].exchangePenalties!.find(p => p.penalty)?.penalty?.currencyCode ?? ''
                    const totalChangeFee = changeableTickets.reduce((sum, t) => {
                      const p = t.exchangePenalties!.find(ep => ep.applicability === timing)
                      return sum + (parseFloat(p?.penalty?.amount ?? '0') || 0)
                    }, 0)
                    return (
                      <div className="mt-2 border border-indigo-200 rounded p-3 bg-indigo-50 space-y-1 text-sm">
                        <p className="text-xs font-semibold text-indigo-700 mb-2">
                          Booking Total ({changeableTickets.length} tickets)
                        </p>
                        <div className="grid grid-cols-2 gap-x-4 font-semibold">
                          <span className="text-indigo-700">Total Change Fee</span>
                          <span className="text-indigo-900 text-right">
                            {totalChangeFee.toFixed(2)} {currency}
                          </span>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              <button
                type="button"
                onClick={() => setChangeFareRulesExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 border-t border-gray-200"
              >
                <span>GDS Fare Rules</span>
                <span className="text-gray-400">{changeFareRulesExpanded ? '▲' : '▼'}</span>
              </button>
              {changeFareRulesExpanded && (
                <div className="px-3 pb-3 space-y-3">
                  {refundQuoteData.fareRulesError && (
                    <p className="text-xs text-red-500">Failed to load fare rules from GDS.</p>
                  )}
                  {refundQuoteData.fareRules && refundQuoteData.fareRules.length > 0
                    ? refundQuoteData.fareRules.map((fc, fcIdx) => (
                        <div
                          key={fcIdx}
                          className="border border-gray-200 rounded bg-white"
                        >
                          <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                            <span className="text-xs font-semibold text-gray-700">{fc.originDestination}</span>
                            <span className="ml-2 text-xs text-gray-500">{fc.fareBasis}</span>
                            <span className="ml-2 text-xs text-gray-400">{fc.airline}</span>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {fc.rules.map((rule, rIdx) => (
                              <details
                                key={rIdx}
                                className="group"
                              >
                                <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50">
                                  <span>
                                    {rule.title}
                                    {rule.category ? (
                                      <span className="ml-1 text-gray-400">(Cat {rule.category})</span>
                                    ) : null}
                                  </span>
                                  <span className="text-gray-400 group-open:rotate-90 transition-transform">▶</span>
                                </summary>
                                <pre className="px-3 pb-3 text-xs text-gray-700 whitespace-pre-wrap font-mono leading-relaxed">
                                  {rule.text}
                                </pre>
                              </details>
                            ))}
                          </div>
                        </div>
                      ))
                    : !refundQuoteData.fareRulesError && (
                        <p className="text-xs text-gray-400">No fare rules available.</p>
                      )}
                </div>
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="passengersCount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Number of Passengers (excluding infants)
            </label>
            <input
              id="passengersCount"
              type="number"
              value={passengersCount}
              onChange={e => setPassengersCount((e.target as HTMLInputElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="0"
              min="0"
              readOnly={false}
            />
          </div>
          <div>
            <label
              htmlFor="changeFeePerPassenger"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Change Fee as per Airline (per passenger) ({currency})
            </label>
            <input
              id="changeFeePerPassenger"
              type="number"
              step="0.01"
              value={changeFeePerPassenger}
              onChange={e => setChangeFeePerPassenger((e.target as HTMLInputElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label
              htmlFor="fareDifference"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Fare Difference (for booking as per Sabre) ({currency})
            </label>
            <input
              id="fareDifference"
              type="number"
              step="0.01"
              value={fareDifference}
              onChange={e => setFareDifference((e.target as HTMLInputElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="0.00"
            />
          </div>
          <div>
            <label
              htmlFor="changeCalculatorConsolidatorFeePerPassenger"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Consolidator Fee (per passenger) ({currency})
            </label>
            <input
              id="changeCalculatorConsolidatorFeePerPassenger"
              type="number"
              step="0.01"
              value={changeCalculatorConsolidatorFeePerPassenger}
              onChange={e => setChangeCalculatorConsolidatorFeePerPassenger((e.target as HTMLInputElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="75.00"
            />
          </div>
          <div>
            <label
              htmlFor="totalChangeFee"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Total Amount Due (For Entire Booking) ({currency})
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-50 text-gray-800 text-lg font-semibold">
              {currency} {calculateTotalChangeFee().toFixed(2)}
            </div>
          </div>

          <div className="pt-4">
            <button
              type="button"
              onClick={copyQuoteToClipboard}
              disabled={
                !bookingId ||
                !passengersCount ||
                !changeFeePerPassenger ||
                !fareDifference ||
                !changeCalculatorConsolidatorFeePerPassenger
              }
              className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {copySuccess ? (
                <>
                  <svg
                    className="h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <svg
                    className="h-4 w-4 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                    />
                  </svg>
                  Copy Quote to Clipboard
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {selectedAction === 'change' && (
        <form
          onSubmit={e => handleFormSubmit('change', e)}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Change Fee (PLT Credit FD Paid) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={changeVcCustomerChangeFee}
                onChange={e => setChangeVcCustomerChangeFee((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Change Fee Paid Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={changeVcCustomerChangeFeePaidDate}
                onChange={e => setChangeVcCustomerChangeFeePaidDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Change Fee Paid <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={changeVcSupplierChangeFee}
                onChange={e => setChangeVcSupplierChangeFee((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Change Fee Paid Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={changeVcSupplierChangeFeePaidDate}
                onChange={e => setChangeVcSupplierChangeFeePaidDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PLT Credit Updated PD Fields <span className="text-red-500">*</span>
              </label>
              <select
                value={changeVcPltCreditUpdatedFields}
                onChange={e => setChangeVcPltCreditUpdatedFields((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="vc-yes-completed">VC Yes Completed</option>
                <option value="plt-credit-not-required-method-1">PLT Credit Not Required Method 1</option>
                <option value="plt-credit-yes-completed-method-2">PLT Credit Yes Completed Method 2</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Send Updated Trip Confirmation <span className="text-red-500">*</span>
              </label>
              <select
                value="yes"
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-gray-100"
                required
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">Always set to "Yes" for changes</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Origin <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={changeOrigin}
                onChange={e => setChangeOrigin((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={changeDestination}
                onChange={e => setChangeDestination((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departure Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={changeDepartDate}
                onChange={e => setChangeDepartDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departure Time <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={changeDepartTime}
                onChange={e => setChangeDepartTime((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g. 14:30"
                pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                title="Please enter time in HH:MM format (24-hour)"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
              <input
                type="date"
                value={changeReturnDate}
                onChange={e => setChangeReturnDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Time</label>
              <input
                type="text"
                value={changeReturnTime}
                onChange={e => setChangeReturnTime((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g. 16:45"
                pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                title="Please enter time in HH:MM format (24-hour)"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Record Locator/PNR <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={changeRecordLocatorPnr}
                onChange={e => setChangeRecordLocatorPnr((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Reference No <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={changeAirlineReferenceNo}
                onChange={e => setChangeAirlineReferenceNo((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Ticket No <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={changeETicketNo}
                onChange={e => setChangeETicketNo((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          {/* Stage is determined by the backend based on the action */}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              value={changeNote}
              onChange={e => setChangeNote((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              required
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitBlocked}
              className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Submit Change'}
            </button>
          </div>
        </form>
      )}

      {selectedAction === 'change-qc' && (
        <div className="space-y-4">
          {fetchingBooking && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-blue-800 text-sm">Loading booking details...</p>
            </div>
          )}

          {!fetchingBooking && !bookingDetails && bookingId.trim() && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-yellow-800 text-sm">
                Enter a valid booking ID to load change information for quality check.
              </p>
            </div>
          )}

          {!fetchingBooking && bookingDetails && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Change Information</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Customer Change Fee (PLT Credit FD Paid):</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.voluntaryChange?.vcCustomerChangeFeeCredFdPaid || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Customer Change Fee Paid Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.voluntaryChange?.vcCustomerChangeFeeCredFdPaidDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Supplier Change Fee Paid:</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.voluntaryChange?.vcSupplierChangeFeePaid || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Supplier Change Fee Paid Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.voluntaryChange?.vcSupplierChangeFeePaidDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">PLT Credit Updated PD Fields:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.voluntaryChange?.vcPltCreditUpdatedPdFields || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Send Updated Trip Confirmation:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.voluntaryChange?.vcSendUpdatedTripConfirmation || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Origin:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.origin || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Destination:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.destination || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Departure Date:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.departureDate || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Return Date:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.returnDate || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Departure Time:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.departureALTime || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Return Time:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.returnALTime || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Record Locator/PNR:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.pnr || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Airline Reference No:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.airlineReference || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">E-Ticket No:</span>
                  <span className="ml-2 text-gray-900">{bookingDetails.eticketNumber || 'Not set'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">QC Complete:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.voluntaryChange?.vcQcComplete || 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={e => handleFormSubmit('change-qc', e)}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                QC Complete <span className="text-red-500">*</span>
              </label>
              <select
                value={vcQcComplete}
                onChange={e => setVcQcComplete((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={changeNote}
                onChange={e => setChangeNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitBlocked || fetchingBooking || !bookingId.trim() || !bookingDetails?.voluntaryChange}
                className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? 'Processing...' : 'Submit Change QC'}
              </button>
              {bookingDetails && !bookingDetails.voluntaryChange && (
                <p className="mt-2 text-sm text-red-600">
                  No change data found for this booking. Please complete a change first before performing quality check.
                </p>
              )}
            </div>
          </form>
        </div>
      )}

      {selectedAction === 'cancellation-qc' && (
        <div className="space-y-4">
          {fetchingBooking && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-blue-800 text-sm">Loading booking details...</p>
            </div>
          )}

          {!fetchingBooking && !bookingDetails && bookingId.trim() && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
              <p className="text-yellow-800 text-sm">
                Enter a valid booking ID to load cancellation information for quality check.
              </p>
            </div>
          )}

          {!fetchingBooking && bookingDetails && (
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Cancellation Information</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Lay By Termination Fee:</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.cancellationAndRefund?.xlLayByTerminationFee || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Cancellation Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.xlCancellationDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Partial Booking Cancellation Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.xlPartialBookingCancellationDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">XL Partial Booking E-Ticket Numbers:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.xlPartialBookingEticketNumbers || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Customer Total Paid To Date:</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.cancellationAndRefund?.xlCustomerTotalPaidToDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Stripe Subscription Cancelled:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.xlStripeSubscriptionCancelledAndUnpaidInvoicesVoided ||
                      'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Final Email Sent To Customer:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.xlFinalEmailSentToCustomer || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Cancelled With Supplier Requested Refund:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.xlCancelledWithSupplierRequestedRefund || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Supplier Total Cash Refund:</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.cancellationAndRefund?.rfSupplierTotalCashRefund || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Supplier Expect Refund Back Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.rfSupplierExpectRefundBackDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Refund Protect Refund Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.refundProtectRefundDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Refund Protect Amount Received:</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.cancellationAndRefund?.refundProtectAmountReceived || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Customer Advised Refund Date:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.rfCustomerAdvisedRefundWouldBeSentDate || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Refund Amount To Customer:</span>
                  <span className="ml-2 text-gray-900">
                    ${bookingDetails.cancellationAndRefund?.refundAmountToCustomer || 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Cancellation Reason:</span>
                  <span className="ml-2 text-gray-900">
                    {bookingDetails.cancellationAndRefund?.cancellationReason || 'Not set'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <form
            onSubmit={e => handleFormSubmit('cancellation-qc', e)}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note <span className="text-red-500">*</span>
              </label>
              <textarea
                value={note}
                onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                rows={3}
                required
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={
                  submitBlocked || fetchingBooking || !bookingId.trim() || !bookingDetails?.cancellationAndRefund
                }
                className="w-full px-4 py-2 bg-indigo-600 text-white font-medium rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'loading' ? 'Processing...' : 'Submit Cancellation QC'}
              </button>
              {bookingDetails && !bookingDetails.cancellationAndRefund && (
                <p className="mt-2 text-sm text-red-600">
                  No cancellation data found for this booking. Please complete a cancellation first before performing
                  quality check.
                </p>
              )}
            </div>
          </form>
        </div>
      )}

      {selectedAction === 'cancellation' && (
        <form
          onSubmit={e => handleFormSubmit('cancellation', e)}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lay-by Termination Fee <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={xlLayByTerminationFee}
                onChange={e => setXlLayByTerminationFee((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cancellation Date</label>
              <input
                type="date"
                value={xlCancellationDate}
                onChange={e => setXlCancellationDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partial Booking Cancellation Date</label>
              <input
                type="date"
                value={xlPartialBookingCancellationDate}
                onChange={e => setXlPartialBookingCancellationDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                XL Partial Booking E-Ticket Numbers
              </label>
              <input
                type="text"
                value={xlPartialBookingEticketNumbers}
                onChange={e => setXlPartialBookingEticketNumbers((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Total Paid to Date <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={xlCustomerTotalPaidToDate}
                onChange={e => setXlCustomerTotalPaidToDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stripe Subscription Cancelled & Unpaid Invoices Voided <span className="text-red-500">*</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={xlStripeSubscriptionCancelled}
                  onChange={e => setXlStripeSubscriptionCancelled((e.target as HTMLInputElement).checked)}
                  className="mr-2"
                  required
                />
                Yes
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Final Email Sent To Customer <span className="text-red-500">*</span>
              </label>
              <select
                value={xlFinalEmailSentToCustomer}
                onChange={e => setXlFinalEmailSentToCustomer((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cancelled with Supplier Requested Refund <span className="text-red-500">*</span>
              </label>
              <select
                value={xlCancelledWithSupplierRefund}
                onChange={e => setXlCancelledWithSupplierRefund((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="yes">Yes</option>
                <option value="n/a">N/A</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Total Cash Refund <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={rfSupplierTotalCashRefund}
                onChange={e => setRfSupplierTotalCashRefund((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Expect Refund Back Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={rfSupplierExpectRefundBackDate}
                onChange={e => setRfSupplierExpectRefundBackDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Promised to Customer <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={rfAmountPromisedToCustomer}
                onChange={e => setRfAmountPromisedToCustomer((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              {/* CancellationSchema rejects a promised amount without this date, so the form has to
                  ask for it on the same terms rather than letting the API refuse the submission. */}
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Advised Refund Would Be Sent Date{' '}
                {refundPromisedToCustomer && <span className="text-red-500">*</span>}
              </label>
              <input
                type="date"
                value={rfCustomerAdvisedRefundDate}
                onChange={e => setRfCustomerAdvisedRefundDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required={refundPromisedToCustomer}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cancellation Reason <span className="text-red-500">*</span>
            </label>
            <select
              value={cancellationReason}
              onChange={e => setCancellationReason((e.target as HTMLSelectElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="">Select a reason</option>
              <option value="Within 24 hours">Within 24 hours</option>
              <option value="Voluntary">Voluntary</option>
              <option value="Due to involuntary airline schedule change">
                Due to involuntary airline schedule change
              </option>
              <option value="Default">Default</option>
              <option value="Hardship Policy">Hardship Policy</option>
              <option value="Fraud related">Fraud related</option>
              <option value="PLT Credit - transferring to another booking">
                PLT Credit - transferring to another booking
              </option>
              <option value="Technical Glitch">Technical Glitch</option>
              <option value="Previous history with PLT">Previous history with PLT</option>
              <option value="Did not pass fraud check">Did not pass fraud check</option>
              <option value="Due to Corona Virus">Due to Corona Virus</option>
              <option value="Testing">Testing</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              required
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitBlocked}
              className="w-full px-4 py-2 bg-red-600 text-white font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Submit Cancellation'}
            </button>
          </div>
        </form>
      )}

      {selectedAction === 'credit-redemption' && (
        <form
          onSubmit={e => handleFormSubmit('credit-redemption', e)}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Change Fee (PLT Credit FD Paid) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={vcCustomerChangeFee}
                onChange={e => setVcCustomerChangeFee((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Change Fee Paid Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={vcCustomerChangeFeePaidDate}
                onChange={e => setVcCustomerChangeFeePaidDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Change Fee Paid <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={vcSupplierChangeFee}
                onChange={e => setVcSupplierChangeFee((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Change Fee Paid Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={vcSupplierChangeFeePaidDate}
                onChange={e => setVcSupplierChangeFeePaidDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PLT Credit Updated PD Fields <span className="text-red-500">*</span>
            </label>
            <select
              value={vcPltCreditUpdatedFields}
              onChange={e => setVcPltCreditUpdatedFields((e.target as HTMLSelectElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              required
            >
              <option value="vc-yes-completed">VC Yes Completed</option>
              <option value="plt-credit-not-required-method-1">PLT Credit Not Required Method 1</option>
              <option value="plt-credit-yes-completed-method-2">PLT Credit Yes Completed Method 2</option>
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Origin <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={origin}
                onChange={e => setOrigin((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Destination <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={destination}
                onChange={e => setDestination((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departure Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={departDate}
                onChange={e => setDepartDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Departure Time <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={departTime}
                onChange={e => setDepartTime((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g. 14:30"
                pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                title="Please enter time in HH:MM format (24-hour)"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Date</label>
              <input
                type="date"
                value={returnDate}
                onChange={e => setReturnDate((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Time</label>
              <input
                type="text"
                value={returnTime}
                onChange={e => setReturnTime((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="e.g. 16:45"
                pattern="^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$"
                title="Please enter time in HH:MM format (24-hour)"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Record Locator/PNR <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={airlineVoucherNewPnr}
                onChange={e => setAirlineVoucherNewPnr((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Reference No <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={airlineVoucherAirlineReferenceNumber}
                onChange={e => setAirlineVoucherAirlineReferenceNumber((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                E-Ticket No <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={airlineVoucherEticketNumbers}
                onChange={e => setAirlineVoucherEticketNumbers((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              required
            />
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitBlocked}
              className="w-full px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Submit Credit Redemption'}
            </button>
          </div>
        </form>
      )}

      {selectedAction === 'cancel-to-credit' && (
        <form
          onSubmit={e => handleFormSubmit('cancel-to-credit', e)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Airline Voucher Sent Request to Convert
            </label>
            <select
              value={airlineVoucherSentRequest}
              onChange={e => setAirlineVoucherSentRequest((e.target as HTMLSelectElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="yes">Yes</option>
            </select>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Voucher Redeem Before <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={airlineVoucherRedeemBefore}
                onChange={e => setAirlineVoucherRedeemBefore((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Voucher Expiration <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={airlineVoucherExpiration}
                onChange={e => setAirlineVoucherExpiration((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Voucher Customer Fully Paid
              </label>
              <select
                value={airlineVoucherCustomerFullyPaid}
                onChange={e => setAirlineVoucherCustomerFullyPaid((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Voucher Send Confirmation Email
              </label>
              <select
                value={airlineVoucherSendConfirmationEmail}
                onChange={e => setAirlineVoucherSendConfirmationEmail((e.target as HTMLSelectElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="yes-sent">Yes - Sent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note <span className="text-red-500">*</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote((e.target as HTMLTextAreaElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-Ticket Numbers in Credit</label>
            <input
              type="text"
              value={airlineVoucherEticketNumbersInCredit}
              onChange={e => setAirlineVoucherEticketNumbersInCredit((e.target as HTMLInputElement).value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Airline Fee to Redeem Per Person ($)
              </label>
              <input
                type="number"
                step="0.01"
                value={airlineVoucherAirlineFeeToRedeemPerPerson}
                onChange={e => setAirlineVoucherAirlineFeeToRedeemPerPerson((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PLT Fee to Redeem Per Person ($)</label>
              <input
                type="number"
                step="0.01"
                value={airlineVoucherPltFeeToRedeemPerPerson}
                onChange={e => setAirlineVoucherPltFeeToRedeemPerPerson((e.target as HTMLInputElement).value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={submitBlocked}
              className="w-full px-4 py-2 bg-purple-600 text-white font-medium rounded-md shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {formStatus === 'loading' ? 'Processing...' : 'Submit Converting to Airline Credit'}
            </button>
          </div>
        </form>
      )}

      {formStatus === 'success' && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">Operation completed successfully!</p>
        </div>
      )}

      {formStatus === 'error' && errorMessage && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="mt-6">
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={resetForm}
            disabled={formStatus === 'loading'}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset Form
          </button>
        </div>
      </div>
    </div>
  )
}
